import { forwardRef, useState, type ChangeEvent, type InputHTMLAttributes, type KeyboardEvent } from 'react'
import { GELD_LOCALE, preisText } from '@symphenia/shared-types'
import { geldText, leseZahl } from '@/components/zeilenblock'

/**
 * **Beträge im Preisformat** (AP-35 Nachtrag 8, zuerst im Verbrauch).
 *
 * Angezeigt wird ein Betrag mit Tausender-Trennzeichen und zwei
 * Nachkommastellen, ohne Währungszeichen — `1.234,50` (Auftraggeber
 * 19.9.2026). Beim Bearbeiten steht die Zahl ohne Tausenderpunkt im Feld
 * (`1234,50`), damit sich der Punkt nicht in die Eingabe mischt.
 *
 * Seit AP-32 Nachtrag 12 (20.9.2026) auch im Belegwesen — darum in
 * `components/`, nicht mehr im Projektmodul.
 */

/**
 * Ein Betrag zum Bearbeiten: zwei Nachkommastellen, ohne Tausenderpunkt — leer bei `null`. Ein Einzelpreis
 * trägt bis zu `stellen` Nachkommastellen, wo er sie hat (`12,50`, `0,1234`).
 */
export function geldEingabe(n: number | null | undefined, stellen = 2): string {
  return n === null || n === undefined ? '' : n.toLocaleString(GELD_LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: Math.max(2, stellen), useGrouping: false })
}

/** Die Anzeige ohne Fokus: ein Betrag mit zwei Stellen, ein Einzelpreis (`stellen` > 2) mit bis zu so vielen. */
const anzeigeText = (zahl: number, stellen: number) => (stellen > 2 ? preisText(zahl, stellen) : geldText(zahl))

/**
 * Eingabefeld für einen Betrag. `wert` ist der Text des Entwurfs; ohne Fokus
 * zeigt das Feld ihn im Preisformat (`geldText`), mit Fokus unverändert.
 * `stellen` (Vorgabe 2): für Einzelpreise, die feiner als ein Cent sind.
 *
 * **Nimmt einen `ref` entgegen** (AP-35 Nachtrag 12): Der Speicherbaustein meldet
 * darüber sein Esc-Ziel an (`useFeld().props.ref`). Ohne den `forwardRef` ließe
 * React den `ref` stillschweigend fallen, und Esc setzte ausgerechnet das
 * Preisfeld nicht zurück.
 */
export const GeldFeld = forwardRef<
  HTMLInputElement,
  { wert: string; onWert: (text: string) => void; stellen?: number } & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>
>(function GeldFeld({ wert, onWert, stellen = 2, className = '', ...rest }, ref) {
  const [fokus, setFokus] = useState(false)
  const zahl = leseZahl(wert)
  // Eine unlesbare Eingabe bleibt stehen, wie sie getippt wurde — `geldText` gäbe nichts zurück.
  const anzeige = fokus || zahl === null || Number.isNaN(zahl) ? wert : anzeigeText(zahl, stellen)
  return (
    <input
      {...rest}
      ref={ref}
      className={`${className} text-right tabular-nums`}
      inputMode="decimal"
      value={anzeige}
      onFocus={(e) => {
        setFokus(true)
        rest.onFocus?.(e)
      }}
      onBlur={(e) => {
        setFokus(false)
        // Beim Verlassen in die übliche Form bringen: „12,5" wird „12,50".
        if (zahl !== null && !Number.isNaN(zahl) && geldEingabe(zahl, stellen) !== wert) onWert(geldEingabe(zahl, stellen))
        rest.onBlur?.(e)
      }}
      onChange={(e) => onWert(e.target.value)}
    />
  )
})

/** Die Props aus `useFeld(…).props` — so, wie der Speicherbaustein sie liefert. */
type FeldProps = {
  name: string
  value: string
  disabled?: boolean
  'aria-invalid'?: boolean
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  onBlur: () => void
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void
  ref: (el: HTMLElement | null) => void
}

/**
 * **Betragsfeld für den Speicherbaustein** (AP-37 Nachtrag 1, N1-33): ohne Fokus
 * `1.234,50`, beim Bearbeiten die Zahl ohne Tausenderpunkt. Das Feld dazu mit
 * `schreib: geldEingabe` anlegen, damit es zwei Nachkommastellen trägt. Ein
 * Einzelpreis bekommt `stellen={4}` und `schreib: (w) => geldEingabe(w, 4)`.
 */
export function GeldFeldFuer({
  feld,
  stellen = 2,
  className = '',
  ...rest
}: { feld: FeldProps; stellen?: number } & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'onBlur' | 'name'>) {
  const [fokus, setFokus] = useState(false)
  const zahl = leseZahl(feld.value)
  const anzeige = fokus || zahl === null || Number.isNaN(zahl) ? feld.value : anzeigeText(zahl, stellen)
  return (
    <input
      {...rest}
      {...feld}
      className={`${className} text-right tabular-nums`}
      inputMode="decimal"
      value={anzeige}
      onFocus={(e) => {
        setFokus(true)
        rest.onFocus?.(e)
      }}
      onBlur={() => {
        setFokus(false)
        feld.onBlur()
      }}
    />
  )
}
